import { HttpErrorResponse } from '@angular/common/http'
import { computed, Injectable, signal } from '@angular/core'
import moment from 'moment'

import { OrderDashboard } from '../models/order.entity'
import { OrdersService } from '../orders.service'

interface OrdersDashboardState extends OrderDashboard {
  startDate?: string
  endDate?: string
  isLoading: boolean
  error?: HttpErrorResponse
}

interface DashboardSearchPeriod {
  startDate?: string | Date
  endDate?: string | Date
}

interface DashboardPeriod {
  startDate: string
  endDate: string
}

const DASHBOARD_INITIAL_STATE: OrdersDashboardState = {
  ordersCount: 0,
  itemsSold: 0,
  averageItemsPerOrder: 0,
  averageTotalOrderAmount: 0,
  averageRowsPerOrder: 0,
  ordersPerChannels: {},
  ordersPerStatus: {},
  isLoading: false,
}

@Injectable()
export class OrdersDashboardStore {
  constructor(private ordersService: OrdersService) {}

  private state = signal<OrdersDashboardState>(DASHBOARD_INITIAL_STATE)

  // Selectors
  readonly period = computed(() => ({
    startDate: this.state().startDate,
    endDate: this.state().endDate,
  }))
  readonly startDate = computed(() => this.state().startDate)
  readonly endDate = computed(() => this.state().endDate)
  readonly ordersPerChannels = computed(() => this.state().ordersPerChannels)
  readonly ordersPerStatus = computed(() => this.state().ordersPerStatus)
  readonly dashboard = computed(() => ({
    ordersCount: this.state().ordersCount,
    itemsSold: this.state().itemsSold,
    averageItemsPerOrder: this.state().averageItemsPerOrder,
    averageRowsPerOrder: this.state().averageRowsPerOrder,
    averageTotalOrderAmount: this.state().averageTotalOrderAmount,
    ordersPerChannels: this.state().ordersPerChannels,
    ordersPerStatus: this.state().ordersPerStatus,
  }))
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async search(): Promise<void> {
    const period = this.period()
    const startDate = period.startDate
    const endDate = period.endDate

    if (!period || !startDate || !endDate) {
      return
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService.dashboard$(startDate, endDate).subscribe({
        next: async (data) => {
          resolve()
          this.setDashboard(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async searchByPeriod(period: DashboardSearchPeriod): Promise<void> {
    const parsedPeriod = this.parseSearchPeriod(period)
    const startDate = parsedPeriod.startDate
    const endDate = parsedPeriod.endDate

    if (!parsedPeriod || !startDate || !endDate) {
      return
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService.dashboard$(startDate, endDate).subscribe({
        next: async (data) => {
          this.setDashboardPeriod(data, parsedPeriod)
          resolve()
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  async searchByDays(days: number): Promise<void> {
    const parsedDays = this.parseSearchPeriod({}, days)
    const startDate = parsedDays.startDate
    const endDate = parsedDays.endDate

    if (!parsedDays || !startDate || !endDate) {
      return
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService.dashboard$(startDate, endDate).subscribe({
        next: async (data) => {
          resolve()
          this.setDashboardPeriod(data, parsedDays)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  // Utilities

  private parseSearchPeriod(
    period: DashboardSearchPeriod,
    days = 7,
  ): DashboardPeriod {
    return {
      startDate: moment(period.startDate)
        .subtract(days, 'days')
        .startOf('day')
        .toISOString(),
      endDate: moment(period.endDate).endOf('day').toISOString(),
    }
  }

  // Reducers

  setDashboard(dashboard: OrderDashboard) {
    this.state.set({
      ...this.state(),
      ...dashboard,
      isLoading: false,
      error: undefined,
    })
  }

  setDashboardPeriod(dashboard: OrderDashboard, period: DashboardPeriod) {
    this.state.set({
      ...this.state(),
      ...dashboard,
      ...period,
      isLoading: false,
      error: undefined,
    })
  }

  setStartDate(startDate: string) {
    this.state.set({
      ...this.state(),
      startDate,
      isLoading: false,
      error: undefined,
    })
  }

  setEndDate(endDate: string) {
    this.state.set({
      ...this.state(),
      endDate,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
